/** Maps payment-listings AI plan columns/filters to Report Builder form state. */

const COLUMN_TO_BUILDER_FIELDS = {
  eventName: ['event_name'],
  eventStart: ['event_start'],
  eventUuid: ['event_uuid'],
  orderId: ['order_id'],
  status: ['status'],
  source: ['source'],
  complimentary: ['complimentary'],
  productType: ['product_type'],
  packageName: ['package_name'],
  packageUuid: ['package_uuid'],
  packageRenewal: ['package_renewal'],
  packageOrder: ['package_order'],
  flexPackName: ['flex_pack_name'],
  flexPackUuid: ['flex_pack_uuid'],
  groupOrder: ['group_order'],
  boxOffice: ['box_office'],
  voucher: ['voucher'],
  assignmentType: ['assignment_type'],
  compLabel: ['comp_label'],
  paymentStatus: ['payment_status'],
  datePaid: ['date_paid'],
  promoCode: ['promo_code'],
  invoiceStatus: ['invoice_status'],
  invoiceUuid: ['invoice_uuid'],
  quantity: ['ticket_count'],
  customerEmail: ['customer_email'],
  customerName: ['customer_first_name', 'customer_last_name'],
  paymentType: ['payment_type'],
  paymentMethod: ['payment_option'],
  gross: ['gross'],
  net: ['net'],
  serviceFee: ['service_fee'],
  processingFee: ['processing_fee'],
  salesTax: ['sales_tax'],
  discountApplied: ['discount_applied'],
  total: ['total'],
  pricingTier: ['pricing_tier'],
  pricingTierPrice: ['pricing_tier_price'],
  additionalPayment: ['additional_payment'],
  additionalProcessingFee: ['additional_processing_fee'],
  estimatedAdditionalProcessingFee: ['estimated_additional_processing_fee'],
  additionalServiceFee: ['additional_service_fee'],
  additionalTotalFeeAmount: ['additional_total_fee_amount'],
  seatsAdded: ['seats_added'],
  seatsAddedAt: ['seats_added_at'],
  ticketsScanned: ['tickets_scanned'],
  attendanceRate: ['attendance_rate'],
  ticketsSoldSnapshot: ['tickets_sold_snapshot'],
  weatherTemperature: ['weather_temperature'],
  weatherPrecipitationProbability: ['weather_precipitation_probability'],
  weatherCondition: ['weather_condition'],
}

const UNMAPPABLE_COLUMNS = new Set([
  'buyerType',
  'accountRep',
  'last4',
  'paymentIntentId',
  'basePrice',
  'facilityFee',
  'totalFees',
  'totalFeeAmount',
  'stripeValidationStatus',
  'stripeValidationMismatchReason',
])

const BUILDER_DATE_FILTER_FIELD_IDS = new Set([
  'processed_at',
  'created_at',
  'date_paid',
])

export function formatIsoToReportBuilderDate(iso) {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  const year = parsed.getFullYear()
  return `${month}/${day}/${year}`
}

function parseBuilderFilterDate(value, boundary = 'start') {
  const normalized = String(value || '').trim()
  if (!normalized) return null

  const mmDdYyyy = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mmDdYyyy) {
    const month = Number(mmDdYyyy[1])
    const day = Number(mmDdYyyy[2])
    const year = Number(mmDdYyyy[3])
    const parsed = new Date(year, month - 1, day)
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null
    }
    if (boundary === 'end') {
      parsed.setHours(23, 59, 59, 999)
    } else {
      parsed.setHours(0, 0, 0, 0)
    }
    return parsed
  }

  const timestamp = Date.parse(normalized)
  if (Number.isNaN(timestamp)) return null
  const parsed = new Date(timestamp)
  if (boundary === 'end') {
    parsed.setHours(23, 59, 59, 999)
  } else {
    parsed.setHours(0, 0, 0, 0)
  }
  return parsed
}

/** Reads a between-date filter from Report Builder form state for AI planning. */
export function extractDateRangeFromBuilderFilters(filters = []) {
  const dateFilter = filters.find(
    (filter) =>
      BUILDER_DATE_FILTER_FIELD_IDS.has(String(filter.fieldId || '')) &&
      filter.operator === 'between'
  )
  if (!dateFilter) return null

  const rawValues = Array.isArray(dateFilter.value)
    ? dateFilter.value
    : String(dateFilter.value || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
  if (rawValues.length < 2) return null

  const start = parseBuilderFilterDate(rawValues[0], 'start')
  const end = parseBuilderFilterDate(rawValues[1], 'end')
  if (!start || !end || start.getTime() > end.getTime()) return null

  const startLabel = formatIsoToReportBuilderDate(start.toISOString())
  const endLabel = formatIsoToReportBuilderDate(end.toISOString())

  return {
    from: start.toISOString(),
    to: end.toISOString(),
    rangeLabel: `${startLabel} – ${endLabel}`,
    dateField: dateFilter.fieldId === 'date_paid' ? 'date_paid' : 'order_date',
  }
}

function resolveDateFieldId(dateField, plan = {}) {
  if (dateField === 'date_paid') return 'date_paid'
  if (dateField === 'processed_date' || dateField === 'processed_at') {
    return 'processed_at'
  }

  const reportType = String(plan?.reportType || '').toLowerCase()
  if (
    reportType &&
    /settlement|processed_payment|stripe_processed/.test(reportType)
  ) {
    return 'processed_at'
  }

  // AI order_date matches payment-listings: filter on processed_at (transaction date).
  // Keep Report Builder preview aligned with the grounded AI answer.
  return 'processed_at'
}

function mapColumnId(columnId, dateField, allowedFieldIds, plan = {}) {
  if (columnId === 'createdAt') {
    const mapped = resolveDateFieldId(dateField, plan)
    return allowedFieldIds.has(mapped) ? [mapped] : []
  }

  if (allowedFieldIds.has(columnId)) {
    return [columnId]
  }

  const mapped = COLUMN_TO_BUILDER_FIELDS[columnId]
  if (!mapped) return []

  return mapped.filter((fieldId) => allowedFieldIds.has(fieldId))
}

function mapSingleFieldId(columnId, dateField, allowedFieldIds, plan = {}) {
  const mapped = mapColumnId(columnId, dateField, allowedFieldIds, plan)
  return mapped[0] || null
}

function buildDateRangeFilter(from, to, dateField, plan = {}) {
  const start = formatIsoToReportBuilderDate(from)
  const end = formatIsoToReportBuilderDate(to)
  if (!start || !end) return null
  return {
    fieldId: resolveDateFieldId(dateField, plan),
    operator: 'between',
    value: [start, end],
  }
}

function isOrderListPreviewPlan(plan) {
  const explanation = String(plan?.explanation || '').toLowerCase()
  return (
    plan?.orderListPreview === true ||
    (/\borders?\b/.test(explanation) &&
      (/\ball(?:\s+of)?(?:\s+the)?\s+/.test(explanation) ||
        /\blist\s+of\b/.test(explanation) ||
        /\bshow\s+(?:me\s+)?(?:all\s+)?(?:the\s+)?orders?\b/.test(explanation)))
  )
}

function buildFiltersFromPlan(plan, { from, to }) {
  const filters = plan?.filters || {}
  const builderFilters = []

  const dateFilter = buildDateRangeFilter(from, to, filters.dateField, plan)
  if (dateFilter) builderFilters.push(dateFilter)

  if (filters.statuses?.length) {
    builderFilters.push({
      fieldId: 'status',
      operator: 'in',
      value: filters.statuses,
    })
  }

  if (filters.source) {
    builderFilters.push({
      fieldId: 'source',
      operator: 'eq',
      value: filters.source,
    })
  }

  if (filters.productType) {
    builderFilters.push({
      fieldId: 'product_type',
      operator: 'eq',
      value: filters.productType,
    })
  }

  if (filters.eventUuid) {
    builderFilters.push({
      fieldId: 'event_uuid',
      operator: 'eq',
      value: filters.eventUuid,
    })
  }

  if (filters.complimentary != null) {
    builderFilters.push({
      fieldId: 'complimentary',
      operator: 'eq',
      value: Boolean(filters.complimentary),
    })
  }

  if (filters.packageRenewal != null) {
    builderFilters.push({
      fieldId: 'package_renewal',
      operator: 'eq',
      value: Boolean(filters.packageRenewal),
    })
  }

  return builderFilters
}

export function suggestReportNameFromPlan(plan) {
  const base = String(plan?.explanation || 'AI report').trim()
  if (!base) return 'AI report'
  return base.length > 120 ? `${base.slice(0, 117)}...` : base
}

/**
 * @param {object} plan - AI report plan
 * @param {{ from: string, to: string, rangeLabel?: string, catalogFieldIds?: string[] }} context
 */
export function buildReportBuilderFormFromAiPlan(plan, context = {}) {
  const allowedFieldIds = new Set(context.catalogFieldIds || [])
  const warnings = []
  const selectedFields = []
  const seenFields = new Set()
  const dateField = plan?.filters?.dateField
  const orderListPreview = isOrderListPreviewPlan(plan)

  const groupBy = (plan?.groupBy || [])
    .map((id) => mapSingleFieldId(id, dateField, allowedFieldIds, plan))
    .filter(Boolean)

  const aggregations = orderListPreview
    ? []
    : (plan?.aggregations || [])
        .map((agg) => {
          const fieldId = mapSingleFieldId(
            agg?.fieldId,
            dateField,
            allowedFieldIds,
            plan
          )
          if (!fieldId) {
            warnings.push(
              `Aggregation "${agg?.fieldId}" could not be mapped to Report Builder fields.`
            )
            return null
          }
          return {
            fieldId,
            fn: String(agg?.fn || 'sum'),
            alias: String(agg?.alias || '').trim() || undefined,
          }
        })
        .filter(Boolean)

  const hasAggregations = aggregations.length > 0
  const hasGroupBy = groupBy.length > 0

  for (const columnId of plan?.columnIds || []) {
    if (UNMAPPABLE_COLUMNS.has(columnId)) {
      warnings.push(
        `"${columnId}" is not available in Report Builder and was skipped.`
      )
      continue
    }

    const mapped = mapColumnId(columnId, dateField, allowedFieldIds, plan)
    if (!mapped.length) {
      warnings.push(
        `"${columnId}" could not be mapped to Report Builder fields.`
      )
      continue
    }

    for (const fieldId of mapped) {
      if (seenFields.has(fieldId)) continue
      if (hasGroupBy && groupBy.includes(fieldId)) continue
      seenFields.add(fieldId)
      selectedFields.push(fieldId)
    }
  }

  if (!selectedFields.length && !hasAggregations) {
    for (const fallback of ['order_id', 'processed_at', 'net']) {
      if (allowedFieldIds.has(fallback) && !seenFields.has(fallback)) {
        selectedFields.push(fallback)
        seenFields.add(fallback)
      }
    }
    warnings.push('No AI columns mapped; using default Report Builder columns.')
  }

  if (hasAggregations && !hasGroupBy && !orderListPreview) {
    // Pure summary reports query via aggregations only, but surface the
    // aggregated fields in Selected Fields so the builder form reflects the plan.
    selectedFields.length = 0
    const aggregationFieldIds = new Set()
    for (const agg of aggregations) {
      const fieldId = agg?.fieldId
      if (!fieldId || aggregationFieldIds.has(fieldId)) continue
      aggregationFieldIds.add(fieldId)
      selectedFields.push(fieldId)
    }
  }

  const sorts = orderListPreview
    ? [
        {
          fieldId: resolveDateFieldId(dateField, plan),
          direction: 'desc',
        },
      ]
    : []

  const filters = buildFiltersFromPlan(plan, context)

  if (plan?.filters?.showUnverifiedTransactions === false) {
    warnings.push(
      'Stripe verified-only filtering applies to payment listings only; Report Builder preview uses order data without Stripe validation filters.'
    )
  }

  const rangeLabel = plan?.rangeLabel || context.rangeLabel
  const description = [
    plan?.explanation,
    rangeLabel ? `Period: ${rangeLabel}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    name: suggestReportNameFromPlan(plan),
    description,
    selectedFields,
    filters,
    sorts,
    groupBy,
    aggregations,
    warnings,
  }
}
